import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableWithoutFeedback } from 'react-native';
import { PageContext, KText, KView } from '@/component/RtlLayout';
import isEqual from 'lodash.isequal';
import { useShowProductBenifits } from '@/hooks/abTest/useShowProductBenifits';
import { useJumpToGoodsDetailPage } from '@/common/biz/jump';
import SpecLinePopWrap from '@/page/detail/views/content/components/SpecLinePopWrap';
import { convertStyles } from '@kibt/krn-utils';
import { ButtonWrapper } from '@/component/Button/Button';
import { $k18n } from '@/common/i18n';
import { logger } from '@/common/log';
import SrcStyles from './index.styl';
import iconAddCart from '@/assets/images/cart.png';
import queryString from 'query-string';
import { Image, Space, Tag } from '@kibt/meta-ui-krn';
import { useCouponPush } from '@/hooks/abTest/useCouponPush';
import CropImage from '@/business-components/CropImage';
import { BillionSubsidy } from '@/business-components/BillionSubsidy';
import FlashIcon from '@/page/shoppingCar/views/components/FlashIcon';
import { CouponSmallest } from '@/business-components/CouponCardMini/mini';
import { margin, scaleW } from '@/common/style';

const Styles = convertStyles(SrcStyles);

type BenifitRes = ReturnType<typeof useShowProductBenifits>;

interface IProps {
    productBenifits: BenifitRes['productBenifits'];
    filterCouponList: BenifitRes['filterCouponList'];
    showBenifitsFun: BenifitRes['showBenifitsFun'];
    showFlash?: boolean;
    customExtParams?: any;
    summaryInfo?: any;
    onRefreshItem?: (item: any) => void;
    showEndIn?: boolean;
    halfWebview?: boolean; // 是否打开半屏
    scene?: string; // 对应打开商品详情的发券场景值
    onCardClick?: () => void;
    onCloseSpecPop?: () => void;
    hasCartButton?: boolean;
    productItem: any;
    cT?: number | string;
    toastContent?: string;
    cardLogAction?: string;
    cardLogParams?: any; // 卡片的埋点参数(不区分点击和曝光时使用)
    clickCardLogParams?: any; // 点击卡片的埋点参数
    clickCartLogParams?: any; // 点击加购的埋点参数
    isReco?: boolean;
    increaseCartSkuCount?: any;
}

const RecommendProductCard = (props: IProps) => {
    const {
        showBenifitsFun,
        productBenifits,
        filterCouponList,
        productItem: item,
        hasCartButton = true,
        cT,
        toastContent,
        cardLogAction,
        cardLogParams,
        clickCardLogParams,
        clickCartLogParams,
        customExtParams,
        summaryInfo,
        onRefreshItem,
        showEndIn,
        halfWebview = false,
        scene,
        isReco = false,
        increaseCartSkuCount,
        onCloseSpecPop,
    } = props;
    const [showPop, setShowPop] = useState(false);
    const specLineRef = useRef(null);
    const { couponPush } = useCouponPush();
    const [showFlash, setShowFlash] = useState(props.showFlash && item.activityInfo?.activityId > 0 && [2, 4, 5].includes(item.activityInfo?.status));
    // 12h内展示倒计时，其余时间展示秒杀标签
    const [showFlashCuntDown, setShowFlashCuntDown] = useState(item.activityInfo?.endTime && item.activityInfo?.endTime < 12 * 60 * 60 * 1000);

    // 活动类型2005：达人商品补贴
    const subsidyItem = item?.activityList?.find((v) => v?.marketingActivityType === 2005);
    // const subsidyItem = item?.activityList?.find((v) => v.marketingActivityType === 2005) || {
    //     marketingActivityType: 2005,
    //     endTime: 4 * 60 * 60 * 1000,
    // };
    const [isSubsidyCountDownEnd, setIsSubsidyCountDownEnd] = useState(false);
    const showBillionSubsidy = !isSubsidyCountDownEnd && subsidyItem?.endTime <= 24 * 60 * 60 * 1000;

    useEffect(() => {
        setShowFlash(props.showFlash && item.activityInfo?.activityId > 0 && [2, 4, 5].includes(item.activityInfo?.status));
        setShowFlashCuntDown(item.activityInfo?.endTime && item.activityInfo?.endTime < 12 * 60 * 60 * 1000);
    }, [item.flashSaleEndTime, item.flashSaleStatus, props.showFlash]);

    const getCouponDeductText = (coupon) => {
        if (coupon.discountFeeType === 3) {
            return $k18n({ id: 'free_shipping_upper', text: '包邮' });
        } else if (coupon?.displayMaxDeductAmount) {
            return $k18n({ id: 'k_322805', params: { discount: coupon?.displayMaxDeductAmount } });
        }
        return '';
    };

    const { jumpToGoodsDetailPage } = useJumpToGoodsDetailPage();
    const tags = item.tags || item.salesPoints;
    function goDetail() {
        // jumpUrl 如果接口直接下发了jumpUrl，透传进来，取出其中的分销ID信息透传
        let sellerUserIdInQuery = '';
        let sellerMerchantIdInQuery = '';
        if (item.jumpUrl) {
            const parseUrl = queryString.parseUrl(item.jumpUrl);
            sellerUserIdInQuery = parseUrl.query.sellerUserId || '';
            sellerMerchantIdInQuery = parseUrl.query.merchantId || '';
        }
        if (summaryInfo?.distributionShop && summaryInfo?.userId) {
            sellerUserIdInQuery = summaryInfo?.userId;
        }
        props.onCardClick?.();
        logger.sendClick({
            action: cardLogAction || 'ECO_PRODUCT_CARD',
            params: clickCardLogParams ||
                cardLogParams || {
                    button_type: 'other',
                    btn_type: 0, // 按钮类型，0-商品详情，1-添加至购物车
                    product_id: item.itemId,
                    is_any_and_off: item?.promotionView?.rule?.length > 0 ? 1 : 0,
                    batch_id: item?.promotionView?.promotionId,
                    item_type: item?.retrievalInfo?.itemType,
                    exptag: item?.retrievalInfo?.expTag,
                    llsid: item?.retrievalInfo?.llsid,
                    provider_source: item?.retrievalInfo?.providerSource,
                    media_source: cT + '',
                    media_id: '',
                    tagCode: tags
                        ? tags
                              .slice(0, 1)
                              .map((v) => v.tagCode)
                              .join('-')
                        : '',
                },
        });
        jumpToGoodsDetailPage({
            itemId: item.itemId,
            sellerUserId: sellerUserIdInQuery,
            merchantId: sellerMerchantIdInQuery,
            retrievalInfo: item?.retrievalInfo,
            extParams: customExtParams ?? {
                cT,
            },
            scene: scene || '0',
            halfWebview: halfWebview,
            backEndDispatchUrl: item?.productDetailJumpUrl,
        });
    }

    function goCart() {
        // clickCartLogParams 目前只有商详页reco上报这个点击埋点
        setShowPop(true);
    }
    function goCartLog(addType = 0) {
        clickCartLogParams
            ? logger.sendClick({
                  action: cardLogAction || 'ECO_PRODUCT_CARD',
                  params: clickCartLogParams,
              })
            : logger.sendClick({
                  action: cardLogAction || 'ECO_PRODUCT_CARD',
                  params: {
                      button_type: 'cart',
                      btn_type: 1, // 按钮类型，0-商品详情，1-添加至购物车
                      product_id: item.itemId,
                      add_type: addType,
                  },
              });
    }

    const onCountDownEnd = () => {
        item.salePrice = null;
        item.displaySalePrice = null;
        onRefreshItem(item);
    };

    const imgTimeRef = useRef(null);
    function renderSalePointTags() {
        return tags.length ? (
            <Space gap={4} bs={{ mt: 8 }}>
                {/* 卖点信息 */}
                {tags.slice(0, 1).map((v) => {
                    return (
                        <Tag
                            key={v.tagCode}
                            linearGradient={{
                                colors: ['rgba(255,242,223,1)', '#fff'],
                            }}
                            textStyle={{ color: '#C18E41' }}
                            textProps={{ numberOfLines: 1, ellipsizeMode: 'tail' }}
                        >
                            {v.tips}
                        </Tag>
                    );
                })}
            </Space>
        ) : null;
    }
    return (
        <PageContext.Provider value={{ Styles }}>
            <View style={{ flex: 1 }}>
                <TouchableWithoutFeedback onPress={goDetail} key={item?.itemId}>
                    <View
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: 16,
                            marginBottom: 12,
                            width: scaleW(191),
                        }}
                    >
                        <View className={Styles['good-card-img-wrap']}>
                            <CropImage
                                resizeMethod="resize"
                                resizeMode="stretch"
                                fadeDuration={0}
                                src={item?.templateImageUrls?.[0] || item?.imageUrls?.[0]}
                                className={Styles.productImg}
                            />
                            <View className={Styles.tag_list}>
                                {showBillionSubsidy ? (
                                    <BillionSubsidy
                                        leftTime={subsidyItem.endTime}
                                        onEnd={() => {
                                            setIsSubsidyCountDownEnd(true);
                                        }}
                                    />
                                ) : null}
                                {productBenifits && !showBillionSubsidy
                                    ? productBenifits.map((benifit) => {
                                          return (
                                              showBenifitsFun(benifit.id, item) && (
                                                  <View key={benifit.id}>
                                                      <Image
                                                          className={Styles.tag_small}
                                                          source={benifit.img}
                                                          style={{ width: benifit.width, height: benifit.height }}
                                                          resizeMethod="resize"
                                                      />
                                                  </View>
                                              )
                                          );
                                      })
                                    : null}
                            </View>
                        </View>
                        <View
                            style={{
                                paddingHorizontal: 12,
                                flex: 1,
                                overflow: 'hidden',
                                marginBottom: 12,
                            }}
                        >
                            <View className={Styles['good-card-tags-wrap']}>{renderSalePointTags()}</View>
                            <View
                                className={Styles['product-title']}
                                style={{
                                    marginVertical: 8,
                                }}
                            >
                                {showBenifitsFun('store_free_shipping', item, true) ? (
                                    <View
                                        className={Styles['pruduct-shippingIncluded']}
                                        style={{
                                            backgroundColor: couponPush.couponUsePrompt ? '#4CA7A2' : '#05a300',
                                        }}
                                    >
                                        <KText rtlClassName={'pruduct-shippingIncluded-text'}>
                                            {$k18n({ id: 'free_shipping_upper', text: '包邮' })}
                                        </KText>
                                    </View>
                                ) : null}
                                <KText numberOfLines={2} ellipsizeMode={'tail'} rtlClassName={'product-title-text'}>
                                    {showBenifitsFun('store_free_shipping', item, true) ? (
                                        <KText rtlClassName={'pruduct-shippingIncluded-text-hidden'}>
                                            {$k18n({ id: 'free_shipping_upper', text: '包邮' }) + '    '}
                                        </KText>
                                    ) : null}
                                    {item.title}
                                </KText>
                            </View>

                            <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    overflow: 'hidden',
                                    maxHeight: 22,
                                }}
                            >
                                <View
                                    style={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        flex: 1,
                                    }}
                                >
                                    {
                                        // 展示：秒杀标签
                                        showFlash ? (
                                            <FlashIcon
                                                item={item}
                                                setShowFlash={setShowFlash}
                                                showFlashCuntDown={showFlashCuntDown}
                                                onCountDownEnd={onCountDownEnd}
                                                wrapperStyle={{
                                                    ...margin(0, 4, 5, 0),
                                                }}
                                                showEndIn={showEndIn}
                                            />
                                        ) : null
                                    }

                                    {/* 多件多折标签 */}
                                    {item?.promotionView?.rule?.length > 0 ? (
                                        <View
                                            style={{
                                                height: 18,
                                                alignItems: 'center',
                                                flexDirection: 'row',
                                                backgroundColor: 'rgba(255, 73, 6, 0.12)',
                                                borderRadius: 3,
                                                paddingHorizontal: 4,
                                                marginRight: 4,
                                                marginBottom: 5,
                                            }}
                                        >
                                            <Text numberOfLines={1} ellipsizeMode={'tail'} style={{ maxWidth: 180, fontSize: 11, color: '#ff4906' }}>
                                                {$k18n({
                                                    id: 'k_445183',
                                                    params: {
                                                        X: item?.promotionView?.rule?.[0]?.threshold,
                                                        Y: item?.promotionView?.rule?.[0]?.discount,
                                                    },
                                                    desc: 'Any X get Y% OFF',
                                                })}
                                            </Text>
                                        </View>
                                    ) : null}
                                    {filterCouponList(item.discountCoupon)
                                        ? filterCouponList(item.discountCoupon).map((itemCoupon) => {
                                              return (
                                                  <View
                                                      key={itemCoupon.discountFeeType + itemCoupon.displayMaxDeductAmount}
                                                      style={{ marginBottom: 5, marginRight: 4 }}
                                                  >
                                                      <CouponSmallest text={getCouponDeductText(itemCoupon)} type={itemCoupon.discountFeeType} />
                                                  </View>
                                              );
                                          })
                                        : null}
                                </View>
                            </View>
                            <View style={{ position: 'relative' }}>
                                <KText rtlClassName={['displayPrice', hasCartButton || !item.marketPrice ? 'black-price' : '']}>
                                    {item.displaySalePrice || item.displayItemPrice}
                                </KText>
                                {item.marketPrice ? (
                                    <KView>
                                        <KText
                                            rtlClassName={'displayGuidePrice'}
                                            style={{
                                                textDecorationLine: 'line-through',
                                            }}
                                        >
                                            {item.displayMarketPrice}
                                        </KText>
                                    </KView>
                                ) : null}
                                {hasCartButton ? (
                                    <ButtonWrapper
                                        onPress={goCart}
                                        style={{
                                            position: 'absolute',
                                            right: 0,
                                            bottom: 0,
                                        }}
                                    >
                                        <Image source={iconAddCart} className={Styles.addCart} resizeMethod="resize" />
                                    </ButtonWrapper>
                                ) : null}
                            </View>
                        </View>
                    </View>
                </TouchableWithoutFeedback>
                <SpecLinePopWrap
                    type="cart"
                    showPop={showPop}
                    item={item}
                    itemId={item.itemId}
                    onAddToCartSuccessfullyCb={increaseCartSkuCount}
                    toastContent={toastContent ?? $k18n({ id: 'k_370557', text: '已添加，请稍后下拉刷新' })}
                    extParamsCustom={
                        customExtParams ?? {
                            cT: Number(cT).toString(),
                        }
                    }
                    // 商品卡默认展示券后价
                    showAfterCouponPrice={true}
                    closePop={() => {
                        setShowPop(false);
                        onCloseSpecPop?.();
                    }}
                    addToCartLog={(params) => goCartLog(params.addType)}
                    scene={scene || '0'}
                    isReco={isReco}
                />
            </View>
        </PageContext.Provider>
    );
};
export default React.memo(RecommendProductCard, (preProps, nextProps) => {
    return isEqual(preProps, nextProps);
});
